import React, { useContext, useEffect, useMemo, useState } from 'react';
import phone from "../../../images/phone.svg";
import {ReactSVG} from "react-svg";
import "./VerificationComponentStyles.scss";
import {Button} from "../../../components/Button";
import {Field, FieldProps, Formik} from "formik";
import {TextField} from "@material-ui/core";
import {OutputLink} from "../../../components/OutputLink";
import config from "../../../config";
import {sendIdentificationCode, signQuotation} from "../../../api/identificationApi";
import {TravelQuotation} from "../../../model/quotation";
import InputMask from "react-input-mask";
import {showOrNotTechnicalWorkPage} from "../../MessagePage";
import {strings} from "../../../strings/strings";
import {ProcessingContext} from "../../../contexts/ProcessingContext";
import {TravelQuotationContext} from "../../../contexts/TravelQuotationContext";
import {TravelScenarioContext} from "../../../contexts/TravelScenatioContext";
import {getSnippetByName} from "../../../api/snippetApiClient";
import {ChatWidget} from "../../../model/chatWidget";
import {WIDGET_DURATION, WidgetTooltip} from "../../../App";
import {ProductType} from "../../../api/model/common";

interface VerificationComponent {
    onVerify: (quotation: TravelQuotation, paymentPageUrl: string) => void;
}

export const TravelVerificationComponent = ({onVerify}: VerificationComponent) => {
    const [verification, setVerification] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(config.verification.resendTimeout);
    const [controlCode, setControlCode] = useState();
    const {quotation, setIsQuotationEditable} = useContext(TravelQuotationContext);
    const {currentStep} = useContext(TravelScenarioContext);
    const [errorMessageVerifyCode, setErrorMessageVerifyCode] = useState("");
    const {setProcessingOpen} = useContext(ProcessingContext);

    useEffect(() => {
        if (!verification) return;

        const timer = secondsLeft > 0 && setInterval(() => setSecondsLeft(secondsLeft - 1), 1000);
        return () => clearInterval(timer);
    }, [verification, secondsLeft]);

    React.useEffect(() => {
        let timout: NodeJS.Timeout
        getSnippetByName(ChatWidget.TEMPLATE_HINT_PAYMENT).then(res => {
            setTimeout(() => {
                WidgetTooltip?.open(res.text, WIDGET_DURATION)
            }, 500)
        })

        return () => {
            clearTimeout(timout)
            WidgetTooltip?.close()
        }
    }, [])

    useEffect(() => {
        const verificationConfig = localStorage.getItem("TRAVEL_verification")
        if(verificationConfig){
            const parsed = JSON.parse(verificationConfig)
            if( parsed.id === quotation.id ){
                setControlCode(parsed.controlCode)
                setSecondsLeft(config.verification.resendTimeout);
                setVerification(true);
                setIsQuotationEditable(false);
                WidgetTooltip?.close()
                getSnippetByName(ChatWidget.TEMPLATE_HINT_SMS).then(res => {
                    setTimeout(() => {
                        WidgetTooltip?.open(res.text, WIDGET_DURATION)
                    }, 1000)
                })
            }
        }
    }, [])

    const sendCode = async () => {
        if (showOrNotTechnicalWorkPage()) return;

        try {
            setProcessingOpen(true);
            setErrorMessageVerifyCode("");

            const value = await sendIdentificationCode(quotation.id, ProductType.TRAVEL);

            handleSuccessfulVerification(value.controlCode);
            await showWidgetTooltip(ChatWidget.TEMPLATE_HINT_SMS, 1000);

            localStorage.setItem(
                "TRAVEL_verification",
                JSON.stringify({ id: quotation.id, controlCode: value.controlCode })
            );
        } catch (error) {
            console.error("Error sending identification code:", error);
        } finally {
            setProcessingOpen(false);
        }
    };

    const handleSuccessfulVerification = (controlCode: any) => {
        setControlCode(controlCode);
        setVerification(true);
        setSecondsLeft(config.verification.resendTimeout);
        setIsQuotationEditable(false);
        WidgetTooltip?.close();
    };

    const showWidgetTooltip = async (snippetName: string, delay: number) => {
        try {
            const res = await getSnippetByName(snippetName);
            setTimeout(() => WidgetTooltip?.open(res.text, WIDGET_DURATION), delay);
        } catch (error) {
            console.warn("Failed to fetch tooltip snippet:", error);
        }
    };

    const verifyCode = (otpCode: string) => {
        if (showOrNotTechnicalWorkPage(true)) return;
        setProcessingOpen(true);
        setErrorMessageVerifyCode("");
        signQuotation(quotation.id, currentStep.code, otpCode, ProductType.TRAVEL).then(value => {
            if (value.error === true) {
                const errorMessage = new RegExp(/Error:\s+([^;]*)/).exec(value.errorMessage)?.[1];
                const message = errorMessage || strings.unkwonwnError;
                setErrorMessageVerifyCode(message);
                setProcessingOpen(false);
            } else {
                onVerify(value.quotation, value.paymentPageUrl);
                setProcessingOpen(false);
            }
        });
    };

    const getPrintoutUrl = () => {
        return `${config.API_URL}/travel/printout?quotationId=${quotation.id}`;
    }

    const sendAgainBtnLbl = useMemo(() => {
        if (secondsLeft > 0) return `${strings.sendAgain} (${secondsLeft})`;
        return strings.sendAgain;
    }, [secondsLeft]);

    return (
        <div className="verification-component">
            <div className={'verification-component__header'}>
                <div className={"verification-component__header__icon"}>
                    <ReactSVG src={phone}/>
                </div>
                <div className={"verification-component__header__text"}>
                    {strings.verifyPhone}
                </div>
            </div>
            <div className={'verification-component__body'}>

                {!verification && (
                    <div className={"verification-component__body__pre-verification"}>
					<span className={"verification-component__body__pre-verification__message"}>
						{strings.verifyDescription1}
                        <span className={"verification-component__body_accent verification-component__body_bold"}>
							{quotation.travelWebContract.primaryInsured.mobileNumber}
						</span>
                        {strings.verifyDescription2}</span>
                        <Button id={'getCodeBtn'} label={strings.getCode} onClickListener={() => sendCode()}/>
                    </div>
                )}

                {verification && (
                    <div className={"verification-component__body__verification"}>
                        <span className="verification-component__body__verification__message">
                            <span className="verification-component__body__pre-verification__message">
                            {strings.preVerificationMessageReadOne}
                            <a id={'verificationComponentBodyLinkCasco'}
                                       className={'verification-component__body_accent verification-component__body--link'}
                                       href={getPrintoutUrl()}
                                       target="_blank"
                                       rel="noopener noreferrer">
                                          {strings.preVerificationMessageTravel} 
                                    </a>
                            {strings.preVerificationMessageSign}
                            </span>
                        </span>    
                        <div className={"verification-component__body__verification__submit-block"}>
               
                            <Formik initialValues={{
                                code: "W"
                            }} onSubmit={values => {
                                verifyCode(values.code);
                            }}>
                                {formProps => (
                                    <form onSubmit={formProps.handleSubmit}>
                                        <div
                                            className={"verification-component__body__verification__submit-block__code-input"}>
                                            <div
                                                className={"verification-component__body__verification__submit-block__code-input__input"}>
                                                <Field name={"code"}>
                                                    {({field, form, meta}: FieldProps) => (
                                                        <InputMask
                                                            {...field}
                                                            mask={"W99999"}
                                                            maskChar={"_"}
                                                            alwaysShowMask={true}>
                                                            {//@ts-ignore
                                                            (field: any) => (
                                                                <TextField {...field}
                                                                           fullWidth={true}
                                                                           helperText={
                                                                               errorMessageVerifyCode !== '' ? errorMessageVerifyCode : `${strings.controlCode} ${controlCode}`
                                                                           }
                                                                           error={errorMessageVerifyCode !== ''}
                                                                           variant="outlined"
                                                                           label={strings.code}
                                                                           InputProps={{
                                                                               ...field.InputProps,
                                                                               shrink: true,
                                                                           }}/>)}
                                                        </InputMask>
                                                    )}
                                                </Field>
                                            </div>

                                            <div
                                                className={"verification-component__body__verification__submit-block__code-input__button"}>
                                                <Button id={'confirmOrderBtn'}
                                                        label={strings.confirm}
                                                        onClickListener={() => formProps.submitForm()}/>
                                            </div>
                                        </div>
                                    </form>
                                )}
                            </Formik>
                        </div>

                        <div className={"verification-component__body__verification__resend"}>
                            <OutputLink label={sendAgainBtnLbl}
                                        onClickListener={() => {
                                            sendCode();
                                        }}
                                        variant={"grey"}
                                        uppercase={true}
                                        disabled={secondsLeft > 0}/>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};
